import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  getCompareRange,
  getPeriodRange,
  toISTDateString,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';

interface Args {
  outletId: string | 'all';
  period: PeriodKey;
  by: 'units' | 'gross_sales';
  limit?: number;
}

export interface CategoryRow {
  categoryName: string;
  units: number;
  sales: number;
  avgPrice: number;
  itemCount: number;
  metric: Metric;
}

interface Result {
  rows: CategoryRow[];
  current: DateRange;
  previous: DateRange;
}

async function fetchRange(outletId: string | 'all', range: DateRange) {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));
  let q = marketing
    .from('mv_item_daily')
    .select('item_id, category_name, units, gross_sales')
    .gte('date', start)
    .lte('date', end);
  if (outletId !== 'all') q = q.eq('outlet_id', outletId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as Array<{
    item_id: string;
    category_name: string | null;
    units: number;
    gross_sales: number;
  }>;
}

function rollup(rows: Awaited<ReturnType<typeof fetchRange>>) {
  const byCat = new Map<
    string,
    { name: string; units: number; sales: number; items: Set<string> }
  >();
  for (const r of rows) {
    const name = r.category_name ?? '(uncategorized)';
    const e = byCat.get(name) ?? { name, units: 0, sales: 0, items: new Set<string>() };
    e.units += Number(r.units ?? 0);
    e.sales += Number(r.gross_sales ?? 0);
    e.items.add(r.item_id);
    byCat.set(name, e);
  }
  return byCat;
}

export function useTopCategories({ outletId, period, by, limit = 10 }: Args) {
  const current = getPeriodRange(period === 'custom' ? '7d' : period);
  const previous = getCompareRange(current, 'previous');

  return useQuery<Result>({
    queryKey: ['top-categories', outletId, by, current.start.toISOString()],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([
        fetchRange(outletId, current),
        fetchRange(outletId, previous),
      ]);
      const cur = rollup(curRows);
      const prev = rollup(prevRows);
      const pick = (e: { units: number; sales: number }) => (by === 'units' ? e.units : e.sales);

      const rows: CategoryRow[] = Array.from(cur.values())
        .map((c) => {
          const p = prev.get(c.name);
          return {
            categoryName: c.name,
            units: c.units,
            sales: c.sales,
            avgPrice: c.units > 0 ? c.sales / c.units : 0,
            itemCount: c.items.size,
            metric: computeMetric(pick(c), p ? pick(p) : 0),
          };
        })
        .sort((a, b) => b.metric.current - a.metric.current)
        .slice(0, limit);

      return { rows, current, previous };
    },
    staleTime: 60_000,
  });
}
